import { useQuery } from '@tanstack/react-query';
import { Scale, TrendingUp } from 'lucide-react';
import { api, type ComparisonRow } from '../lib/api.js';
import { hours, num, pct } from '../lib/format.js';
import {
  Callout,
  ErrorState,
  PageHeader,
  Panel,
  SkeletonMetrics,
  Spinner,
} from '../ui/index.js';
import { ChartPalette, DataTable, Meter, PairedBars } from '../components/charts.js';

/**
 * The cross-tenant comparison (FR-RPT-02).
 *
 * This is the report that surfaces what no firm-wide number showed: teams with
 * the same remit, near-identical volume and the same SLA target, clearing work
 * at very different speeds.
 */
export function Comparison() {
  const q = useQuery({
    queryKey: ['comparison'],
    queryFn: () => api.get<{ tenants: ComparisonRow[]; days: number }>('/api/v1/reports/comparison'),
  });

  if (q.isLoading) {
    return (
      <div className="mx-auto max-w-[1480px] p-4 sm:p-6">
        <PageHeader title="Team comparison" description="All six fulfilment groups side by side" />
        <SkeletonMetrics count={3} />
        <Spinner label="Comparing teams" />
      </div>
    );
  }

  if (q.error) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <ErrorState error={q.error} />
        <p className="mt-3 text-xs leading-relaxed text-ink-secondary">
          Cross-tenant reporting requires a <strong>platform_admin</strong> grant — it is the one
          report that reads across every group, so it is gated and audited on every call. Switch
          identity from the menu in the top right.
        </p>
      </div>
    );
  }
  if (!q.data) return null;

  const rows = q.data.tenants;
  const withMedian = rows.filter((r) => r.medianResolutionHours !== null);
  const sorted = [...withMedian].sort(
    (a, b) => (a.medianResolutionHours ?? 0) - (b.medianResolutionHours ?? 0),
  );
  const fastest = sorted[0];
  const slowest = sorted.at(-1);
  const gap =
    fastest && slowest && (fastest.medianResolutionHours ?? 0) > 0
      ? (slowest.medianResolutionHours ?? 0) / (fastest.medianResolutionHours ?? 1)
      : null;

  return (
    <div className="mx-auto max-w-[1480px] p-4 sm:p-6">
      <ChartPalette />

      <PageHeader
        title="Team comparison"
        description={`Last ${q.data.days} days, all six fulfilment groups side by side`}
      />

      {gap !== null && gap > 1.5 && fastest && slowest && (
        <div className="mb-5 overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
          <div className="flex items-start gap-3.5 border-l-[3px] border-l-accent p-4">
            <span
              aria-hidden
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-tint text-accent"
            >
              <TrendingUp className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-ink">
                {gap.toFixed(1)}× spread between the fastest and slowest queue
              </h2>
              <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-ink-secondary">
                <strong className="text-ink">{slowest.tenantName}</strong> has a median resolution
                of {hours(slowest.medianResolutionHours)} against{' '}
                <strong className="text-ink">{fastest.tenantName}</strong> at{' '}
                {hours(fastest.medianResolutionHours)}. Nothing in a firm-wide average shows this —
                it only appears once the queues are separated, which is the entire reason this page
                exists.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Resolution time by team"
          subtitle="Median against p90 — the gap between them is the tail"
        >
          <PairedBars
            rows={rows.map((r) => ({
              label: r.tenantName,
              a: r.medianResolutionHours,
              b: r.p90ResolutionHours,
            }))}
            labelA="Median"
            labelB="p90"
            format={(v) => hours(v)}
          />
        </Panel>

        <Panel
          title="SLA breach rate"
          subtitle="Share of closed tickets that missed their target"
          footer={
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              A team breaching around half its work under a single target is a signal about the
              target, not only about the team — which is exactly the open question for Publications.
            </p>
          }
        >
          <MeterList
            rows={rows.map((r) => ({
              label: r.tenantName,
              value: r.slaBreachRate,
              tone:
                (r.slaBreachRate ?? 0) > 0.4
                  ? 'danger'
                  : (r.slaBreachRate ?? 0) > 0.2
                    ? 'warn'
                    : 'default',
            }))}
          />
        </Panel>

        <Panel
          title="Share of elapsed time spent blocked"
          subtitle="How much of each team's clock is somebody else's wait"
        >
          <MeterList
            rows={rows.map((r) => ({
              label: r.tenantName,
              value: r.blockedShare,
              tone: (r.blockedShare ?? 0) > 0.5 ? 'warn' : 'default',
            }))}
          />
        </Panel>

        <Panel
          title="Conversation load"
          subtitle="Median messages per ticket — how much of the work is dialogue"
          footer={
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              Conversational work behaves differently from transactional work, and a resolution
              deadline may be the wrong measure for it.
            </p>
          }
        >
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.tenantId} className="flex items-center justify-between gap-3">
                <span className="truncate text-xs text-ink-secondary">{r.tenantName}</span>
                <span className="nums shrink-0 text-sm font-medium text-ink">
                  {r.medianRepliesPerTicket === null ? '—' : r.medianRepliesPerTicket.toFixed(1)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel
          title="All measures"
          subtitle="Every group, every figure, in one table"
          className="xl:col-span-2"
          flush
        >
          <div className="p-4 pt-0">
            <DataTable
              head={[
                'Team',
                'Tickets',
                'Median',
                'p90',
                'First response',
                'Breach rate',
                'Blocked share',
                'Open',
              ]}
              rows={rows.map((r) => [
                r.tenantName,
                num(r.volume),
                hours(r.medianResolutionHours),
                hours(r.p90ResolutionHours),
                hours(r.medianFirstResponseHours),
                pct(r.slaBreachRate),
                pct(r.blockedShare),
                num(r.openBacklog),
              ])}
            />
          </div>
        </Panel>
      </div>

      <Callout tone="brand" icon={<Scale />} className="mt-4">
        This report reads across every tenant, so it requires a platform-admin grant and each call
        is written to the security audit log.
      </Callout>
    </div>
  );
}

function MeterList({
  rows,
}: {
  rows: { label: string; value: number | null; tone: 'default' | 'warn' | 'danger' }[];
}) {
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <span className="truncate text-xs text-ink-secondary">{r.label}</span>
          <Meter value={r.value} tone={r.tone} />
        </li>
      ))}
    </ul>
  );
}
